import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';

const COSEmployeeGoalsReview = () => {
  const baseUrl = API_BASE_URL;
  const [goals, setGoals] = useState([]);
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const employeeId = location.state?.employeeId || id;
  const [employeeName, setEmployeeName] = useState('');
  const navigate = useNavigate();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filteredGoals, setFilteredGoals] = useState([]);

  const filterGoalsByYear = (goalsList, year) => {
    return goalsList.filter((goal) => {
      if (goal.year !== undefined && goal.year !== null) {
        return goal.year === year;
      }

      const goalDate =
        goal.created_at ||
        goal.updated_at ||
        goal.start_date ||
        goal.end_date ||
        goal.date_created;
      if (!goalDate) {
        return true;
      }

      const goalYear = new Date(goalDate).getFullYear();
      return goalYear === year;
    });
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes blinkUnreviewed {
        0% { background-color: #fef3c7 !important; }
        50% { background-color: #fbbf24 !important; }
        100% { background-color: #fef3c7 !important; }
      }
      .unreviewed-row {
        animation: blinkUnreviewed 1.5s infinite !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [hodScore, setHodScore] = useState('');
  const [agreedScore, setAgreedScore] = useState('');
  const [modalError, setModalError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [agreedModalOpen, setAgreedModalOpen] = useState(false);
  const [viewGoal, setViewGoal] = useState(null);

  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(
          `${baseUrl}/api/employees/${employeeId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        const completedGoals = (
          Array.isArray(response?.data?.employee?.goals)
            ? response.data.employee.goals
            : []
        ).filter(
          (goal) => goal.status === 'complete' || goal.status === 'completed',
        );
        setGoals(completedGoals);
        const emp = response?.data?.employee;
        setEmployeeName(
          emp?.name ||
            emp?.full_name ||
            [emp?.first_name, emp?.last_name].filter(Boolean).join(' ') ||
            '',
        );

        setFilteredGoals(filterGoalsByYear(completedGoals, selectedYear));
      } catch (err) {
        setError('Failed to fetch KPIs.');
      } finally {
        setLoading(false);
      }
    };
    fetchGoals();
  }, [baseUrl, employeeId, selectedYear]);

  useEffect(() => {
    if (goals.length > 0) {
      setFilteredGoals(filterGoalsByYear(goals, selectedYear));
    }
  }, [selectedYear, goals]);

  const handleReviewClick = (goal) => {
    setSelectedGoal(goal);
    setHodScore('');
    setModalError('');
    setModalOpen(true);
  };

  const handleAgreedReviewClick = (goal) => {
    setSelectedGoal(goal);
    setAgreedScore('');
    setModalError('');
    setAgreedModalOpen(true);
  };

  const handleViewClick = (goal) => {
    setViewGoal(goal);
    setViewModalOpen(true);
  };

  const getFinalScore = (g) =>
    g.agreed_score !== undefined && g.agreed_score !== null
      ? Number(g.agreed_score)
      : Number(g.coo_score) || 0;

  const generateReportData = () => {
    const kraMap = {};
    filteredGoals.forEach((g) => {
      const kraName =
        (g.key_result_area &&
          (g.key_result_area.name || g.key_result_area)) ||
        g.kra_name ||
        'Uncategorized';
      if (!kraMap[kraName]) kraMap[kraName] = [];
      kraMap[kraName].push(g);
    });

    const totalScore = filteredGoals.reduce(
      (sum, g) => sum + getFinalScore(g),
      0,
    );
    const totalMetric = filteredGoals.reduce(
      (sum, g) => sum + (Number(g.metric) || 0),
      0,
    );
    const percentage =
      totalMetric > 0 ? ((totalScore / totalMetric) * 100).toFixed(2) : '0.00';

    return {
      employeeName,
      year: selectedYear,
      kras: Object.entries(kraMap),
      totalScore,
      totalMetric,
      percentage,
      goals: filteredGoals,
    };
  };

  const generatePDF = async () => {
    try {
      const reportData = generateReportData();
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');

      const loadLogoDataUrl = async () => {
        const response = await fetch('/celebration logo.png');
        const blob = await response.blob();
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      };

      let logoDataUrl = null;
      try {
        logoDataUrl = await loadLogoDataUrl();
      } catch (err) {
        console.warn('Logo could not be loaded for PDF watermark:', err);
      }

      const drawWatermark = (pageNumber = 1) => {
        if (!logoDataUrl) return;
        doc.setPage(pageNumber);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const wmWidth = 120;
        const wmHeight = 90;
        const x = (pageWidth - wmWidth) / 2;
        const y = (pageHeight - wmHeight) / 2;
        doc.addImage(logoDataUrl, 'PNG', x, y, wmWidth, wmHeight);
      };

      const primaryColor = [59, 130, 246];
      const darkGray = [55, 65, 81];
      const lightGray = [156, 163, 175];

      const wrapText = (text, maxWidth) => {
        const words = String(text || '').split(' ');
        const lines = [];
        let currentLine = '';

        for (const word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const testWidth = doc.getTextWidth(testLine);

          if (testWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }

        if (currentLine) lines.push(currentLine);
        return lines;
      };

      const cssColorToRgb = (color) => {
        if (!color || typeof color !== 'string') return [229, 231, 235];
        const value = color.trim();
        const hexMatch = value.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
        if (hexMatch) {
          let hex = hexMatch[1];
          if (hex.length === 3) {
            hex = hex.split('').map((ch) => ch + ch).join('');
          }
          return [
            parseInt(hex.slice(0, 2), 16),
            parseInt(hex.slice(2, 4), 16),
            parseInt(hex.slice(4, 6), 16),
          ];
        }

        const rgbMatch = value.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
        if (rgbMatch) {
          return [
            Math.max(0, Math.min(255, parseInt(rgbMatch[1], 10))),
            Math.max(0, Math.min(255, parseInt(rgbMatch[2], 10))),
            Math.max(0, Math.min(255, parseInt(rgbMatch[3], 10))),
          ];
        }
        return [229, 231, 235];
      };

      const drawTable = (headers, rows, startY, maxWidth = 180) => {
        const colWidths = [58, 38, 20, 28, 36];
        const rowHeight = 8;
        const headerHeight = 10;

        doc.setFillColor(...primaryColor);
        doc.rect(15, startY, maxWidth, headerHeight, 'F');

        let x = 15;
        headers.forEach((header, i) => {
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text(header, x + 2, startY + 6);
          x += colWidths[i];
        });

        let currentY = startY + headerHeight;
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);

        rows.forEach((row, rowIndex) => {
          if (rowIndex % 2 === 0) {
            doc.setFillColor(249, 250, 251);
            doc.rect(15, currentY, maxWidth, rowHeight, 'F');
          }

          x = 15;
          let maxLines = 1;
          row.forEach((cell, colIndex) => {
            if (cell && typeof cell === 'object' && cell.type === 'rag') {
              const ragLabel = String(cell.label || '');
              const [r, g, b] = cssColorToRgb(cell.color);
              const boxX = x + 2;
              const boxY = currentY + 2.2;
              const boxW = 8;
              const boxH = 4;
              doc.setFillColor(r, g, b);
              doc.rect(boxX, boxY, boxW, boxH, 'F');
              doc.setDrawColor(203, 213, 225);
              doc.rect(boxX, boxY, boxW, boxH);

              const wrappedText = wrapText(ragLabel, colWidths[colIndex] - 13);
              maxLines = Math.max(maxLines, wrappedText.length || 1);
              wrappedText.forEach((line, lineIndex) => {
                doc.text(line, x + 12, currentY + 5 + lineIndex * 3.5);
              });
            } else {
              const cellText = String(cell || '');
              const wrappedText = wrapText(cellText, colWidths[colIndex] - 4);
              maxLines = Math.max(maxLines, wrappedText.length);
              wrappedText.forEach((line, lineIndex) => {
                doc.text(line, x + 2, currentY + 5 + lineIndex * 3.5);
              });
            }
            x += colWidths[colIndex];
          });

          currentY += Math.max(rowHeight, maxLines * 3.5 + 2);
        });

        return currentY;
      };

      let yPosition = 20;
      drawWatermark(1);

      doc.setTextColor(...primaryColor);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Performance Report', 105, yPosition, { align: 'center' });

      yPosition += 15;
      doc.setTextColor(...darkGray);
      doc.setFontSize(16);
      doc.setFont(undefined, 'normal');
      doc.text(
        `for ${employeeName} - Year: ${selectedYear}`,
        105,
        yPosition,
        { align: 'center' },
      );

      yPosition += 8;

      doc.setFillColor(248, 250, 252);
      doc.rect(15, yPosition, 180, 25, 'F');
      doc.setDrawColor(...primaryColor);
      doc.rect(15, yPosition, 180, 35);

      yPosition += 8;
      doc.setTextColor(...darkGray);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Performance Summary', 20, yPosition);

      yPosition += 8;
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.text(
        `Total Final Score: ${reportData.totalScore} / ${reportData.totalMetric}`,
        20,
        yPosition,
      );

      yPosition += 5;
      doc.text(`Performance Percentage: ${reportData.percentage}%`, 20, yPosition);

      yPosition += 5;
      doc.text(`Number of Goals: ${reportData.goals.length}`, 20, yPosition);

      yPosition += 20;

      reportData.kras.forEach(([kraName, kraGoals]) => {
        const kraTotal = kraGoals.reduce((s, g) => s + getFinalScore(g), 0);
        const kraMetric = kraGoals.reduce(
          (s, g) => s + (Number(g.metric) || 0),
          0,
        );

        if (yPosition > 250) {
          doc.addPage();
          drawWatermark(doc.internal.getNumberOfPages());
          yPosition = 20;
        }

        doc.setFillColor(...primaryColor);
        doc.rect(15, yPosition, 180, 12, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`KRA: ${kraName} (${kraTotal}/${kraMetric})`, 20, yPosition + 8);

        yPosition += 15;

        const headers = ['Activity/KPI', 'Standard', 'Metric', 'RAG', 'Final Score'];
        const rows = kraGoals.map((goal) => {
          const finalScore =
            goal.agreed_score !== undefined && goal.agreed_score !== null
              ? goal.agreed_score
              : goal.coo_score;

          return [
            goal.activity || 'N/A',
            goal.performance_standard || 'N/A',
            goal.metric || 'N/A',
            {
              type: 'rag',
              label: goal.rag_color || '',
              color: goal.progress_indicator || '#e5e7eb',
            },
            finalScore !== undefined ? finalScore : 'Not Reviewed',
          ];
        });

        rows.push(['KRA Total', '', '', '', `${kraTotal}/${kraMetric}`]);
        yPosition = drawTable(headers, rows, yPosition);
        yPosition += 15;
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setTextColor(...lightGray);
        doc.setFontSize(8);
        doc.text('Generated by Performance Management System', 15, 290);
        doc.text(`Page ${i} of ${pageCount}`, 175, 290, { align: 'right' });
      }

      doc.save(
        `Performance Report for ${employeeName} for ${selectedYear}.pdf`,
      );
    } catch (error) {
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal) return;
    const maxScore = selectedGoal.metric;
    const parsedScore = Number(hodScore);
    if (isNaN(parsedScore) || parsedScore < 0) {
      setModalError('Please enter a valid score.');
      return;
    }
    if (parsedScore > maxScore) {
      setModalError(
        `COO score cannot be greater than the goal metric (${maxScore}).`,
      );
      return;
    }
    setSubmitting(true);
    setModalError('');
    try {
      const token = Cookies.get('token');
      await axios.post(
        `${baseUrl}/api/goals/${selectedGoal.id}/score/`,
        { coo_score: parsedScore },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setGoals((prev) =>
        prev.map((g) =>
          g.id === selectedGoal.id ? { ...g, coo_score: parsedScore } : g,
        ),
      );
      setFilteredGoals((prev) =>
        prev.map((g) =>
          g.id === selectedGoal.id ? { ...g, coo_score: parsedScore } : g,
        ),
      );
      setModalOpen(false);
      setSelectedGoal(null);
      setHodScore('');
    } catch (err) {
      setModalError('Failed to submit COO score.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAgreedScoreSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal) return;
    const maxScore = selectedGoal.metric;
    const parsedScore = Number(agreedScore);
    if (isNaN(parsedScore) || parsedScore < 0) {
      setModalError('Please enter a valid score.');
      return;
    }
    if (parsedScore > maxScore) {
      setModalError(
        `Agreed score cannot be greater than the KPI metric (${maxScore}).`,
      );
      return;
    }
    setSubmitting(true);
    setModalError('');
    try {
      const token = Cookies.get('token');
      await axios.post(
        `${baseUrl}/api/goals/${selectedGoal.id}/score/`,
        { agreed_score: parsedScore },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      const updateGoals = (list) =>
        list.map((g) =>
          g.id === selectedGoal.id ? { ...g, agreed_score: parsedScore } : g,
        );
      setGoals((prev) => updateGoals(prev));
      setFilteredGoals((prev) => updateGoals(prev));
      setAgreedModalOpen(false);
      setSelectedGoal(null);
      setAgreedScore('');
    } catch (err) {
      setModalError('Failed to submit agreed score.');
    } finally {
      setSubmitting(false);
    }
  };

  const getDocumentUrl = (goal) => {
    const path = goal?.goal_documents?.document;
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  return (
    <div
      data-sidebar-skin="dark"
      data-header-skin="light"
      data-navbar-brand-skin="dark"
      data-sidebar-state="expand"
    >
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
              >
                <i className="ti ti-arrow-left" /> Back
              </button>
              <div>
                <h4 className="fw-bold mb-1">Employee KPIs Review</h4>
                <p className="text-muted mb-0 fs-13">
                  Review completed employee KPIs and assign COO scores
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0 fs-14 text-muted">Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              {filteredGoals.length > 0 && (
                <button
                  type="button"
                  onClick={generatePDF}
                  className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                  title="Download PDF Report"
                >
                  <i className="ti ti-file-text fs-16" />
                  <i className="ti ti-download fs-14" />
                  <span>Download PDF</span>
                </button>
              )}

              {employeeName && (
                <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 fs-14 rounded-pill">
                  <i className="ti ti-user me-1" />
                  {employeeName}
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="card shadow-sm p-5 text-center">
              <div
                className="spinner-border text-primary mx-auto mb-2"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mb-0 fs-13">Loading KPIs...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger shadow-sm" role="alert">
              <i className="ti ti-alert-circle me-2" />
              {error}
            </div>
          ) : filteredGoals.length === 0 ? (
            <div className="card shadow-sm p-5 text-center text-muted">
              <i className="ti ti-target-off fs-36 mb-2 text-secondary opacity-50" />
              No completed KPIs found for {selectedYear}.
            </div>
          ) : (
            <>
              {(() => {
                const kraMap = {};
                filteredGoals.forEach((g) => {
                  const kraName =
                    (g.key_result_area &&
                      (g.key_result_area.name || g.key_result_area)) ||
                    g.kra_name ||
                    'Uncategorized';
                  if (!kraMap[kraName]) kraMap[kraName] = [];
                  kraMap[kraName].push(g);
                });
                const grouped = Object.entries(kraMap);

                return grouped.map(([kra, kraGoals], gidx) => (
                  <div key={kra || gidx} className="card shadow-sm mb-4">
                    <div className="card-header bg-light border-bottom py-3">
                      <h6 className="card-title mb-0 fw-bold text-dark">
                        KRA: <span className="text-primary">{kra}</span>
                      </h6>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Activity / Key Performance Indicator</th>
                            <th>Performance Standard / Target</th>
                            <th style={{ width: '120px' }}>RAG</th>
                            <th>KPI Metric</th>
                            <th>COO Score</th>
                            <th>Agreed Score</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kraGoals.map((goal, idx) => {
                            const currentScore = goal.coo_score;
                            const isUnreviewed =
                              currentScore === 0 || currentScore === undefined;
                            return (
                              <tr
                                key={goal.id || idx}
                                className={isUnreviewed ? 'unreviewed-row' : ''}
                              >
                                <td>{goal.activity || '[No Activity]'}</td>
                                <td>
                                  {goal.performance_standard !== undefined
                                    ? goal.performance_standard
                                    : '-'}
                                </td>
                                <td>
                                  <span
                                    className="badge w-100 text-white"
                                    style={{
                                      backgroundColor:
                                        goal.progress_indicator || '#6c757d',
                                    }}
                                  >
                                    {goal.rag_color || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  {goal.metric !== undefined ? goal.metric : '-'}
                                </td>
                                <td>
                                  {goal.coo_score !== undefined
                                    ? goal.coo_score
                                    : '-'}
                                </td>
                                <td>
                                  {goal.agreed_score !== undefined
                                    ? goal.agreed_score
                                    : '-'}
                                </td>
                                <td className="text-center">
                                  <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                    {goal.flagged && (
                                      <span className="badge bg-danger-subtle text-danger border border-danger-subtle d-inline-flex align-items-center gap-1">
                                        <i className="ti ti-flag-filled" />
                                        Flagged
                                      </span>
                                    )}

                                    {isUnreviewed ? (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-success"
                                        onClick={() => handleReviewClick(goal)}
                                      >
                                        Review
                                      </button>
                                    ) : (
                                      <span className="badge bg-success-subtle text-success border border-success-subtle">
                                        Reviewed
                                      </span>
                                    )}

                                    {!isUnreviewed &&
                                    (goal.agreed_score === 0 ||
                                      goal.agreed_score === undefined) ? (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={() =>
                                          handleAgreedReviewClick(goal)
                                        }
                                      >
                                        Agree Score
                                      </button>
                                    ) : goal.agreed_score !== 0 &&
                                      goal.agreed_score !== undefined ? (
                                      <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                                        Agreed
                                      </span>
                                    ) : null}

                                    <button
                                      type="button"
                                      className="btn btn-sm btn-icon btn-light text-primary"
                                      onClick={() => handleViewClick(goal)}
                                      title="View Details"
                                    >
                                      <i className="ti ti-eye" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          <tr className="bg-light fw-bold">
                            <td
                              colSpan={3}
                              className="text-end text-uppercase fs-12 text-muted"
                            >
                              KRA Final
                            </td>
                            <td colSpan={4} className="text-dark">
                              {kraGoals.reduce(
                                (s, g) => s + getFinalScore(g),
                                0,
                              )}{' '}
                              /{' '}
                              {kraGoals.reduce(
                                (s, g) => s + (Number(g.metric) || 0),
                                0,
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ));
              })()}

              {filteredGoals.length > 0 &&
                (() => {
                  const totalScore = filteredGoals.reduce(
                    (sum, g) => sum + getFinalScore(g),
                    0,
                  );
                  const totalMetric = filteredGoals.reduce(
                    (sum, g) => sum + (Number(g.metric) || 0),
                    0,
                  );
                  const pct =
                    totalMetric > 0
                      ? ((totalScore / totalMetric) * 100).toFixed(2)
                      : '0.00';
                  return (
                    <div className="card shadow-sm mb-4 bg-primary-subtle border border-primary-subtle">
                      <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <span className="fw-bold text-dark fs-16">
                          Final Total
                        </span>
                        <div className="d-flex align-items-center gap-3">
                          <span className="fw-bold text-dark fs-16">
                            Total Score:{' '}
                            <span className="text-primary">
                              {totalScore} / {totalMetric}
                            </span>
                          </span>
                          <span className="badge bg-primary fs-14 px-3 py-2 rounded-pill">
                            {pct}%
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
            </>
          )}
        </div>

        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All
            Rights Reserved
          </p>
        </div>
      </div>

      {modalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Review Goal</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setModalOpen(false)}
                  aria-label="Close"
                />
              </div>
              {selectedGoal && (
                <form onSubmit={handleModalSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label text-muted fs-13 mb-1">
                        Activity
                      </label>
                      <div className="fw-semibold text-dark">
                        {selectedGoal.activity}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label text-muted fs-13 mb-1">
                        KPI Metric
                      </label>
                      <div className="fw-semibold text-dark">
                        {selectedGoal.metric}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label
                        htmlFor="coo-score-input"
                        className="form-label fw-medium"
                      >
                        COO Score
                      </label>
                      <input
                        id="coo-score-input"
                        type="number"
                        min="0"
                        max={selectedGoal.metric}
                        value={hodScore}
                        onChange={(e) => setHodScore(e.target.value)}
                        className="form-control"
                        required
                        disabled={submitting}
                      />
                    </div>
                    {modalError && (
                      <div className="alert alert-danger py-2 mb-0" role="alert">
                        {modalError}
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setModalOpen(false)}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          />
                          Submitting...
                        </>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {agreedModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Agree Final Score</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setAgreedModalOpen(false)}
                  aria-label="Close"
                />
              </div>
              {selectedGoal && (
                <form onSubmit={handleAgreedScoreSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label text-muted fs-13 mb-1">
                        Activity
                      </label>
                      <div className="fw-semibold text-dark">
                        {selectedGoal.activity}
                      </div>
                    </div>
                    <div className="row g-2 mb-3 bg-light p-2 rounded border">
                      <div className="col-4">
                        <span className="text-muted fs-12 d-block">Metric</span>
                        <strong className="text-dark fs-14">
                          {selectedGoal.metric}
                        </strong>
                      </div>
                      <div className="col-4">
                        <span className="text-muted fs-12 d-block">
                          Self Score
                        </span>
                        <strong className="text-dark fs-14">
                          {selectedGoal.score}
                        </strong>
                      </div>
                      <div className="col-4">
                        <span className="text-muted fs-12 d-block">
                          COO Score
                        </span>
                        <strong className="text-dark fs-14">
                          {selectedGoal.coo_score}
                        </strong>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label
                        htmlFor="agreed-score-input"
                        className="form-label fw-medium"
                      >
                        Agreed Final Score
                      </label>
                      <input
                        id="agreed-score-input"
                        type="number"
                        min="0"
                        max={selectedGoal.metric}
                        value={agreedScore}
                        onChange={(e) => setAgreedScore(e.target.value)}
                        className="form-control"
                        required
                        disabled={submitting}
                      />
                    </div>
                    {modalError && (
                      <div className="alert alert-danger py-2 mb-0" role="alert">
                        {modalError}
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setAgreedModalOpen(false)}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          />
                          Submitting...
                        </>
                      ) : (
                        'Agree Score'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {viewModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Goal Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setViewModalOpen(false)}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                {viewGoal && (
                  <>
                    {viewGoal.flagged && viewGoal.flagging_reason && (
                      <div
                        className="alert alert-danger d-flex align-items-start gap-2 mb-3"
                        role="alert"
                      >
                        <i className="ti ti-flag-filled fs-18 mt-1" />
                        <div>
                          <strong className="d-block">Flag Reason</strong>
                          <span style={{ whiteSpace: 'pre-wrap' }}>
                            {viewGoal.flagging_reason}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="table-responsive border rounded mb-3">
                      <table className="table table-striped mb-0">
                        <tbody>
                          {[
                            { label: 'Activity / KPI', value: viewGoal.activity },
                            {
                              label: 'Performance Standard / Target',
                              value: viewGoal.performance_standard,
                            },
                            {
                              label: 'Metric',
                              value: viewGoal.metric || viewGoal.score,
                            },
                            { label: 'Self Score', value: viewGoal.score },
                            {
                              label: 'COO Score',
                              value:
                                viewGoal.coo_score !== undefined
                                  ? viewGoal.coo_score
                                  : '-',
                            },
                            {
                              label: 'Agreed Score',
                              value:
                                viewGoal.agreed_score !== undefined &&
                                viewGoal.agreed_score !== null
                                  ? viewGoal.agreed_score
                                  : '-',
                            },
                          ].map((row) => (
                            <tr key={row.label}>
                              <th
                                style={{ width: '35%' }}
                                className="text-muted fw-semibold fs-14"
                              >
                                {row.label}
                              </th>
                              <td
                                className="fw-medium text-dark"
                                style={{ whiteSpace: 'pre-wrap' }}
                              >
                                {row.value}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {getDocumentUrl(viewGoal) && (
                      <a
                        href={getDocumentUrl(viewGoal)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-success"
                      >
                        <i className="ti ti-download me-1" /> View Document
                      </a>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setViewModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default COSEmployeeGoalsReview;
